import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle2, ChevronRight } from 'lucide-react';
import { references as mockReferences } from '../data';
import { fetchCmsCollection, normalizeReference } from '../lib/cms';

const ReferenceDetail = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [allReferences, setAllReferences] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadProject() {
      setLoading(true);
      const rawRefs = await fetchCmsCollection('references', mockReferences);
      const normalizedRefs = rawRefs.map(normalizeReference);
      setAllReferences(normalizedRefs);

      const found = normalizedRefs.find(r => r.slug === slug || r.id === slug) || 
                    mockReferences.find(r => r.slug === slug);

      setProject(found || null);
      setLoading(false);
    }
    loadProject();
  }, [slug]);

  useEffect(() => {
    if (project) {
      document.title = `Green Energy Consulting - ${project.title}`;
    }
  }, [project]);

  if (loading) {
    return (
      <div className="pt-32 pb-20 text-center text-slate-500 font-medium">
        Henter projektdetaljer...
      </div>
    );
  }

  if (!project) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center overflow-hidden">
        <motion.div 
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0 z-0"
        >
          <img src={project.image} alt={project.company} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-900/70"></div>
        </motion.div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link 
              to="/#referencer" 
              className="inline-flex items-center gap-2 text-emerald-400 font-bold text-sm uppercase tracking-widest mb-6 hover:text-white transition-colors group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              Tilbage til referencer
            </Link>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">{project.title}</h1>
            <p className="text-xl text-slate-300 max-w-2xl">{project.company}</p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Projektbeskrivelse</h2>
                {typeof project.fullDescription === 'string' && project.fullDescription.startsWith('<') ? (
                  <div 
                    className="prose prose-slate text-lg text-slate-600 leading-relaxed mb-8"
                    dangerouslySetInnerHTML={{ __html: project.fullDescription }}
                  />
                ) : (
                  <p className="text-lg text-slate-600 leading-relaxed mb-8">
                    {project.fullDescription}
                  </p>
                )}
                
                <div className="h-px bg-slate-100 mb-12"></div>
                
                {project.keyResults && project.keyResults.length > 0 && (
                  <>
                    <h3 className="text-2xl font-bold text-slate-900 mb-6">Nøgleresultater</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {project.keyResults.map((result, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <CheckCircle2 className="w-6 h-6 text-emerald-500 shrink-0 mt-0.5" />
                          <span className="text-slate-700 font-medium">{result}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </motion.div>
            </div>

            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-slate-50 p-8 border border-slate-100 sticky top-32"
              >
                {project.technologies && project.technologies.length > 0 && (
                  <>
                    <h3 className="text-lg font-bold text-slate-900 uppercase tracking-widest mb-6">Teknologier & Kompetencer</h3>
                    <div className="flex flex-wrap gap-2 mb-8">
                      {project.technologies.map((tech, idx) => (
                        <span 
                          key={idx} 
                          className="px-3 py-1 bg-white border border-slate-200 text-slate-600 text-xs font-bold uppercase tracking-wider"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </>
                )}
                <div className="space-y-6">
                  <h3 className="text-lg font-bold text-slate-900 uppercase tracking-widest mb-4">Har du et lignende projekt?</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6">
                    Kontakt os for en uforpligtende snak om hvordan vi kan hjælpe dig med projektledelse eller engineering.
                  </p>
                  <Link 
                    to="/#kontakt" 
                    className="block w-full bg-emerald-600 hover:bg-emerald-500 text-white text-center font-bold py-4 transition-colors"
                  >
                    Kontakt os i dag
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Next Projects */}
      <section className="py-20 bg-slate-50 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-12">Se andre projekter</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {allReferences
              .filter(ref => ref.slug !== slug && ref.id !== project.id)
              .slice(0, 3)
              .map((ref, idx) => (
                <Link 
                  key={idx} 
                  to={`/referencer/${ref.slug}`}
                  className="group bg-white border border-slate-200 overflow-hidden flex flex-col"
                >
                  <div className="h-48 overflow-hidden relative">
                    <img src={ref.image} alt={ref.company} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-slate-900/20 group-hover:bg-slate-900/0 transition-colors"></div>
                  </div>
                  <div className="p-6 flex-grow">
                    <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-[0.2em] block mb-2">{ref.company}</span>
                    <h3 className="text-lg font-bold text-slate-900 mb-3 group-hover:text-emerald-600 transition-colors">{ref.title}</h3>
                  </div>
                  <div className="px-6 py-4 border-t border-slate-50 flex items-center justify-between text-sm font-bold text-slate-400 group-hover:text-emerald-600 transition-colors">
                    <span>Læs mere</span>
                    <ChevronRight className="w-4 h-4" />
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReferenceDetail;
